import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useConfirm } from '@/components/ConfirmProvider';
import { 
  Loader2, 
  ExternalLink, 
  Link2, 
  Unlink, 
  ChevronDown,
  Check,
  AlertCircle,
  Search
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google Search Console Icon
const GoogleSearchConsoleIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" className="inline-block">
    <path fill="#458CF5" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const IntegrationsSection = ({ websiteId, token }) => {
  const confirm = useConfirm();
  // State
  const [loading, setLoading] = useState(true);
  const [scStatus, setScStatus] = useState({
    connected: false,
    google_email: null,
    linked_property: null
  });
  const [scProperties, setScProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  // Check if sc_connected=true in URL (after OAuth callback)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('sc_connected') === 'true') {
      toast.success('Google Search Console connected successfully!');
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh status
      fetchScStatus();
    }
    if (params.get('error')) {
      const errorDetail = params.get('detail') || '';
      const errorMsg = params.get('error');
      
      // Show appropriate error message
      if (errorMsg === 'oauth_failed') {
        if (errorDetail.includes('redirect_uri_mismatch')) {
          toast.error('OAuth Error: Redirect URI not registered in Google Cloud Console. Please add the callback URL to your OAuth credentials.');
        } else if (errorDetail.includes('invalid_grant')) {
          toast.error('OAuth Error: Invalid or expired authorization code. Please try again.');
        } else {
          toast.error(`OAuth failed: ${errorDetail || 'Unknown error. Check browser console for details.'}`);
        }
      } else {
        toast.error(`Connection failed: ${errorMsg}`);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Fetch Search Console status
  const fetchScStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/integrations/search-console/status`, {
        params: { website_id: websiteId, token }
      });
      setScStatus(res.data);
      
      if (res.data.connected && res.data.linked_property) {
        setSelectedProperty(res.data.linked_property);
      }
    } catch (err) {
      console.error('Failed to fetch SC status:', err);
    } finally {
      setLoading(false);
    }
  }, [websiteId, token]);

  useEffect(() => {
    fetchScStatus();
  }, [fetchScStatus]);

  // Handle Google Auth
  const handleGoogleAuth = async () => {
    try {
      const res = await axios.get(`${API}/integrations/search-console/auth-url`, {
        params: { website_id: websiteId, token }
      });
      // Redirect to Google OAuth
      window.location.href = res.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start Google authentication');
    }
  };

  // Fetch Search Console properties
  const fetchProperties = async () => {
    setLoadingProperties(true);
    try {
      const res = await axios.get(`${API}/integrations/search-console/properties`, {
        params: { website_id: websiteId, token }
      });
      setScProperties(res.data.properties || []);
      setShowPropertyDropdown(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch properties');
    } finally {
      setLoadingProperties(false);
    }
  };

  // Save selected property
  const handleSaveProperty = async () => {
    if (!selectedProperty) {
      toast.error('Please select a property');
      return;
    }
    
    setSavingProperty(true);
    try {
      await axios.post(`${API}/integrations/search-console/link`, null, {
        params: { 
          website_id: websiteId, 
          property_url: selectedProperty,
          token 
        }
      });
      toast.success('Search Console property linked!');
      setScStatus(prev => ({ ...prev, linked_property: selectedProperty }));
      setShowPropertyDropdown(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save property');
    } finally {
      setSavingProperty(false);
    }
  };

  // Unlink Google account
  const handleUnlink = async () => {
    const ok = await confirm({
      title: 'Unlink Google Search Console?',
      description: 'Search keywords will stop syncing. You can reconnect later — no historical data is deleted.',
      confirmLabel: 'Unlink',
      icon: Unlink,
    });
    if (!ok) return;

    setUnlinking(true);
    try {
      await axios.delete(`${API}/integrations/search-console/unlink`, {
        params: { website_id: websiteId, token }
      });
      toast.success('Google Search Console disconnected');
      setScStatus({
        connected: false,
        google_email: null,
        linked_property: null
      });
      setSelectedProperty('');
      setScProperties([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlink');
    } finally {
      setUnlinking(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-content">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-content" data-testid="integrations-section">
      
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Integrations</h2>

      {/* Google Search Console Integration */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <GoogleSearchConsoleIcon />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Google Search Console</h3>
              <p className="text-sm text-gray-600">
                Integrate with Google Search Console to get all of your important search results stats 
                such as keyword phrases people find your site with.
              </p>
            </div>
          </div>

          {!scStatus.connected ? (
            // Not connected - Show connect button
            <div className="mt-4">
              <Button
                onClick={handleGoogleAuth}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="connect-search-console-btn"
              >
                <Link2 className="w-4 h-4 mr-2" />
                Connect Google Account
              </Button>
              
              {/* Setup Instructions */}
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="text-sm font-medium text-amber-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Setup Required
                </h4>
                <p className="text-xs text-amber-800 mb-2">
                  Before connecting, ensure the following redirect URI is added to your Google Cloud Console OAuth credentials:
                </p>
                <code className="block text-xs bg-amber-100 px-2 py-1 rounded text-amber-900 break-all">
                  {`${process.env.REACT_APP_BACKEND_URL}/api/integrations/search-console/oauth-callback`}
                </code>
                <p className="text-xs text-amber-700 mt-2">
                  Also enable the <strong>Search Console API</strong> in your Google Cloud project.
                </p>
              </div>
            </div>
          ) : (
            // Connected - Show linked account and property selector
            <div className="mt-4 space-y-4">
              {/* Linked Google Account */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-700">Linked Google account</span>
                  <span className="text-sm font-medium text-gray-900">{scStatus.google_email || 'Connected'}</span>
                </div>
                <button
                  onClick={handleUnlink}
                  disabled={unlinking}
                  className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                  data-testid="unlink-search-console-btn"
                >
                  {unlinking ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Unlink className="w-4 h-4" />
                  )}
                  Unlink Google account
                </button>
              </div>

              {/* Property Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Console Property
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Select the Google Search Console property you would like to pull keyword data from. 
                  If you don't see your domain, <a 
                    href="https://search.google.com/search-console" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-700"
                  >
                    set it up and verify on Search Console first
                    <ExternalLink className="w-3 h-3 inline ml-1" />
                  </a>.
                </p>

                <div className="flex gap-3">
                  {/* Property Dropdown */}
                  <div className="relative flex-1">
                    <button
                      onClick={() => {
                        if (scProperties.length === 0) {
                          fetchProperties();
                        } else {
                          setShowPropertyDropdown(!showPropertyDropdown);
                        }
                      }}
                      disabled={loadingProperties}
                      className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-left"
                      data-testid="property-dropdown-btn"
                    >
                      <span className={selectedProperty ? 'text-gray-900' : 'text-gray-500'}>
                        {loadingProperties ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading properties...
                          </span>
                        ) : selectedProperty ? selectedProperty.replace('sc-domain:', '') : 'Select a property'}
                      </span>
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </button>

                    {/* Dropdown Menu */}
                    {showPropertyDropdown && scProperties.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                        {scProperties.map((prop) => (
                          <button
                            key={prop.site_url}
                            onClick={() => {
                              setSelectedProperty(prop.site_url);
                              setShowPropertyDropdown(false);
                            }}
                            className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center justify-between ${
                              selectedProperty === prop.site_url ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                            }`}
                          >
                            <div className="flex flex-col">
                              <span>{prop.site_url.replace('sc-domain:', '')}</span>
                              {prop.permission_level === 'siteUnverifiedUser' && (
                                <span className="text-xs text-amber-600">⚠ Unverified - verify in Search Console</span>
                              )}
                              {prop.permission_level === 'siteOwner' && (
                                <span className="text-xs text-green-600">✓ Owner</span>
                              )}
                              {prop.permission_level === 'siteFullUser' && (
                                <span className="text-xs text-blue-600">✓ Full access</span>
                              )}
                            </div>
                            {selectedProperty === prop.site_url && (
                              <Check className="w-4 h-4 text-indigo-600" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* No properties message */}
                    {showPropertyDropdown && scProperties.length === 0 && !loadingProperties && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-4">
                        <div className="flex items-center gap-2 text-gray-500">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-sm">No properties found. Please add and verify your site in Google Search Console first.</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={handleSaveProperty}
                    disabled={!selectedProperty || savingProperty || selectedProperty === scStatus.linked_property}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-6"
                    data-testid="save-property-btn"
                  >
                    {savingProperty ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>

                {/* Currently linked property indicator */}
                {scStatus.linked_property && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
                    <Check className="w-4 h-4" />
                    <span>Currently linked: <strong>{scStatus.linked_property.replace('sc-domain:', '')}</strong></span>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Google Looker Studio Connector - Placeholder */}
      <Card className="opacity-60">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Search className="w-6 h-6 text-gray-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Google Looker Studio Connector
                <span className="ml-2 text-xs font-normal bg-gray-200 text-gray-600 px-2 py-1 rounded">Coming Soon</span>
              </h3>
              <p className="text-sm text-gray-600">
                Connect your analytics data to Google Looker Studio for advanced reporting and visualizations.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationsSection;
